import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Loader2, CopyX, ShieldCheck, AlertTriangle, Fingerprint, MonitorSmartphone, Server, ExternalLink } from "lucide-react";
import Header from "@/components/Header";
import { useLang } from "@/i18n";
import api, { apiError } from "@/lib/api";

const SECTIONS = [
  { id: "serial", de: "Gleiche Seriennummer", en: "Same serial number", icon: Fingerprint, hint_de: "Zweiter physischer Identifikator — dieselbe Seriennummer auf mehreren Datensätzen deutet auf einen Datenfehler oder ein doppelt erfasstes Gerät hin.", hint_en: "Same serial on multiple records — likely a data error." },
  { id: "hostname", de: "Gleicher Hostname", en: "Same hostname", icon: Server, hint_de: "Mehrere Datensätze mit identischem Hostnamen (v. a. ohne Hardware-ID = Sheet-Inkonsistenz).", hint_en: "Multiple records with identical hostname." },
  { id: "teamviewer_id", de: "Gleiche TeamViewer-ID", en: "Same TeamViewer ID", icon: MonitorSmartphone, hint_de: "Dieselbe TV-ID auf mehreren Geräten — meist ein veralteter/falscher Eintrag.", hint_en: "Same TV ID on multiple devices." },
];

export default function Duplicates() {
  const { lang } = useLang();
  const de = lang === "de";
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/devices/duplicates");
      setData(r.data);
    } catch (err) { toast.error(apiError(err)); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const total = data?.total_groups ?? 0;

  const DeviceRow = ({ d }) => (
    <div className="flex items-center justify-between gap-3 py-1.5 px-2 rounded hover:bg-gray-50">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-mono text-sm text-gray-900 truncate">{d.device_code || "—"}</span>
          {!d.device_uid && (
            <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-700 border border-amber-500/30">{de ? "ohne HW-ID" : "no HW-ID"}</span>
          )}
          <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border ${d.source === "sheet" ? "bg-[#c00000]/10 text-[#c00000] border-[#c00000]/30" : "bg-gray-50 text-gray-600 border-gray-200"}`}>{d.source || "—"}</span>
        </div>
        <div className="text-[11px] text-gray-600 font-mono truncate">
          {d.location_name || "—"}
          {d.device_uid ? ` · ${d.device_uid.slice(0, 20)}` : ""}
          {d.hostname ? ` · ${d.hostname}` : ""}
        </div>
      </div>
      <button
        data-testid={`dup-open-${d.device_code}`}
        onClick={() => nav(`/d/${encodeURIComponent(d.device_code || d.device_uid || "")}`)}
        className="shrink-0 flex items-center gap-1 text-xs text-[#c00000] border border-[#c00000]/30 bg-[#c00000]/5 hover:bg-[#c00000]/15 rounded px-2 py-1 transition-colors"
      >
        <ExternalLink className="w-3 h-3" /> {de ? "Öffnen" : "Open"}
      </button>
    </div>
  );

  return (
    <div className="min-h-screen bg-white text-gray-800">
      <Header />
      <div className="w-full px-4 sm:px-6 py-6">
        <div className="flex items-center gap-3 mb-1">
          <CopyX className="w-6 h-6 text-amber-600" />
          <h1 className="text-2xl font-bold text-gray-900">{de ? "Duplikat-Report" : "Duplicate report"}</h1>
        </div>
        <p className="text-sm text-gray-600 mb-6">{de ? "Verdächtige Dopplungen in den Stammdaten — Hardware-IDs sind bereits eindeutig geschützt, hier geht es um sekundäre Merkmale." : "Suspicious duplicates in master data — hardware IDs are already uniquely protected."}</p>

        {loading ? (
          <div className="flex items-center gap-2 text-gray-600"><Loader2 className="w-4 h-4 animate-spin" /> {de ? "Prüft…" : "Checking…"}</div>
        ) : total === 0 ? (
          <div data-testid="dup-clean" className="flex flex-col items-center justify-center gap-3 py-20 text-center">
            <div className="w-14 h-14 rounded-full bg-[#c00000]/10 border border-[#c00000]/30 flex items-center justify-center">
              <ShieldCheck className="w-7 h-7 text-[#c00000]" />
            </div>
            <div className="text-lg font-semibold text-gray-900">{de ? "Keine Duplikate gefunden" : "No duplicates found"}</div>
            <div className="text-sm text-gray-600">{de ? "Alle Seriennummern, Hostnamen und TeamViewer-IDs sind eindeutig." : "All serials, hostnames and TeamViewer IDs are unique."}</div>
          </div>
        ) : (
          <div className="space-y-8" data-testid="dup-report">
            {SECTIONS.map((sec) => {
              const groups = data?.[sec.id] || [];
              const Icon = sec.icon;
              return (
                <div key={sec.id} data-testid={`dup-section-${sec.id}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <Icon className="w-4 h-4 text-[#c00000]" />
                    <h2 className="text-lg font-semibold text-gray-900">{de ? sec.de : sec.en}</h2>
                    <span className="text-sm text-gray-600">({groups.length})</span>
                  </div>
                  <p className="text-xs text-gray-600 mb-3">{de ? sec.hint_de : sec.hint_en}</p>
                  {groups.length === 0 ? (
                    <div className="text-sm text-gray-600 py-2">{de ? "Keine." : "None."}</div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {groups.map((g) => {
                        const conflict = sec.id === "serial" && g.distinct_hardware >= 2;
                        return (
                          <div key={g.value} data-testid={`dup-group-${sec.id}-${g.value}`} className={`rounded-xl p-4 border ${conflict ? "bg-red-500/5 border-red-500/40" : "bg-gray-50 border-gray-200"}`}>
                            <div className="flex items-center justify-between mb-2">
                              <div className="font-mono text-sm text-gray-900 break-all">{g.value}</div>
                              <span className="text-xs font-mono text-gray-600 shrink-0 ml-2">×{g.count}</span>
                            </div>
                            {conflict && (
                              <div className="flex items-center gap-1.5 text-xs text-red-700 mb-2">
                                <AlertTriangle className="w-3.5 h-3.5" />
                                {de ? `Gleiche Seriennummer auf ${g.distinct_hardware} verschiedenen Hardware-IDs!` : `Same serial on ${g.distinct_hardware} different hardware IDs!`}
                              </div>
                            )}
                            <div className="divide-y divide-gray-100">
                              {g.devices.map((d, i) => <DeviceRow key={i} d={d} />)}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
